import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Spin, message } from 'antd';
import classNames from 'classnames';
import { debounce } from 'lodash-es';
import {
  type StatisticPlaygroundDataResContent as PlayGroundResDataType,
  listPlaygroundProvinceCityOptions,
  type AdminDivisionOptionDto,
  type PlaygroundDetailItem,
} from '../../api/playgroundApi';
import { MtvMap, type MarkerProps } from './MtvMap';
import { MTDIcon } from '../../components/Icon';
import { WithModal as PGDetailModal } from '../../components/PGModalCard';
import { ZoomControl } from './ZoomControl';
import {
  zoomLevelConfig,
  getCurrentLevelConfig,
  getNextStep,
  getPrevStep,
  MapLevel,
  getCurrentStepInfo,
  report,
} from './constant';
import { getMarkerDataList, filterPointByBounds } from './utils';
import { PGMarkerItemNew } from './PGMarkItemNew';
import FilterPanel, { type FilterMode } from './FilterPanel';
import styles from './PGMap.module.scss';
import '@antv/l7-component/es/css/index.css';

const MAP_KEY = 'm391f93085054ce68e06e741ca94e14j';

interface Props {
  opts?: { markerSourcePath: string; markerInfoPath: string };
  onReport?(params: { type: string; payload: any }): void;
  register?(callback: any): void;
  initialMapLevel?: MapLevel;
  initialCenter?: [number, number];
  onFullScreenChange?(state: boolean): void;
  lanType?: string;
}

const defaultZoomMtvLevel = zoomLevelConfig.COUNTRY.level?.[0] || 1;

const sceneConfig = {
  logoVisible: false,
};

const defaultCenter = [108.93, 34.27];

const defaultMapConfig = {
  version: '1.8.0',
  l7Version: '0.0.16',
  key: MAP_KEY,
  center: defaultCenter,
  pitch: 0,
  zoom: defaultZoomMtvLevel,
  enableDblclickZoom: false,
  style: 'light',
  plugin: ['ZoomControl'],
};

const PGMapNew = (props: Props) => {
  const loadingCountRef = useRef(0);
  const [loading, setLoadingState] = useState(false);
  const mtvSceneRef = useRef<any>();
  const [markerLists, setMarkerList] = useState<PlayGroundResDataType[]>([]);
  const zoomMtvLevelRef = useRef<number>(defaultZoomMtvLevel);
  const zoomLevelRef = useRef<MapLevel>(zoomLevelConfig.COUNTRY.value);
  const [isLastZoomLevel, setIsLastZoomLevel] = useState(false);
  const [isFullScreen, setFullScreen] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailSerialNumber, setDetailSerialNumber] = useState<string>();
  const [viewBounds, setViewBounds] = useState<any>();
  const getDataRef = useRef<(level: MapLevel) => Promise<void>>();
  const registerRef = useRef<any>();
  // 请求版本号：每次主动发起筛选/清除操作时递增，过期的异步响应（如地图缩放中途）会被丢弃
  const markerGenRef = useRef(0);
  // 屏蔽 zoomchange 请求的标志：程序主动调用 setZoomAndCenter 时设为 true，
  // 防止回缩/跳转过程中途经的中间层级再次触发数据请求
  const ignoreZoomChangeRef = useRef(false);
  // 动画完成后（zoomend）需要执行的回调，替代不可靠的 setTimeout 魔法数字
  const onZoomEndCallbackRef = useRef<(() => void) | null>(null);

  // 筛选相关状态
  const [selectedProvince, setSelectedProvince] = useState<AdminDivisionOptionDto | null>(null);
  const [selectedCity, setSelectedCity] = useState<AdminDivisionOptionDto | null>(null);
  const [provinceOptions, setProvinceOptions] = useState<AdminDivisionOptionDto[]>([]);
  const [cityOptions, setCityOptions] = useState<AdminDivisionOptionDto[]>([]);
  const [highlightSerialNo, setHighlightSerialNo] = useState<string | undefined>();
  // 搜索相关状态（提升管理，使 PC/H5 切换时保留）
  const [selectedSearchItem, setSelectedSearchItem] = useState<PlaygroundDetailItem | null>(null);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  // 当前筛选 tab（提升管理，使 PC/H5 切换时保留）
  const [filterMode, setFilterMode] = useState<FilterMode>('region');

  // 移动端判定：width < 1024px
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    const handleResize = debounce(checkMobile, 30);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  const { opts, onReport, register, onFullScreenChange, initialMapLevel, initialCenter, lanType } = props;
  registerRef.current = register;

  const selectedProvinceRef = useRef(selectedProvince);
  const selectedCityRef = useRef(selectedCity);
  selectedProvinceRef.current = selectedProvince;
  selectedCityRef.current = selectedCity;

  const [defaultMapOption] = useState({
    zoom: initialMapLevel ? zoomLevelConfig[initialMapLevel].level[0] : defaultZoomMtvLevel,
    center: initialCenter ? [...initialCenter] : defaultCenter,
  });

  const mapConfig = useMemo(() => {
    return {
      ...defaultMapConfig,
      ...defaultMapOption,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultMapOption]);

  const lxReport = useMemo(() => {
    return report(onReport);
  }, [onReport]);

  const setLoading = useCallback((state: boolean) => {
    const { current: loadingCount } = loadingCountRef;
    if (state) loadingCountRef.current = loadingCount + 1;
    else {
      const finalLoadingCount = loadingCount - 1;
      if (finalLoadingCount <= 0) loadingCountRef.current = 0;
      else loadingCountRef.current = finalLoadingCount;
    }
    setLoadingState(loadingCountRef.current > 0);
  }, []);

  // 查询数据（无筛选条件）
  getDataRef.current = async (level: MapLevel) => {
    const gen = markerGenRef.current;
    setLoading(true);
    try {
      const pgStatsLists = await getMarkerDataList({ acType: level, lanType: lanType || '' }, opts?.markerSourcePath);
      // 版本号不匹配说明期间有新的操作（如清除/重新筛选），丢弃过期响应
      if (gen !== markerGenRef.current) return;
      if (pgStatsLists) {
        setMarkerList(pgStatsLists);
      } else {
        setMarkerList([]);
      }
    } finally {
      // 无论请求是否已过期，都必须归还本次请求占用的 loading 计数，避免计数泄漏
      setLoading(false);
    }
  };

  const onMapLoad = async (scene: any) => {
    setLoading(false);
    mtvSceneRef.current = scene;
    scene.setMapStatus({
      keyboardEnable: false,
      zoomEnable: false,
    });
    scene?.on('zoomchange', (val: any) => {
      const { data: zoomData } = val || {};
      const { after: currentLevel } = zoomData || {};
      zoomMtvLevelRef.current = currentLevel;
      const matchZoomLevelConfig = getCurrentLevelConfig(currentLevel);
      if (matchZoomLevelConfig?.value !== zoomLevelRef.current) {
        zoomLevelRef.current = matchZoomLevelConfig?.value as MapLevel;
        // 程序主动触发 setZoomAndCenter 期间，忽略途经层级的数据请求
        if (ignoreZoomChangeRef.current) return;
        // 感知筛选状态
        if (selectedProvinceRef.current) {
          // 快照当前版本号，响应回来时检查是否已过期
          const gen = markerGenRef.current;
          getMarkerDataList({
            acType: zoomLevelRef.current,
            provinceAdCode: selectedProvinceRef.current.adCode,
            cityAdCode: selectedCityRef.current?.adCode,
            lanType: lanType || '',
          }).then((markerData) => {
            if (gen !== markerGenRef.current) return;
            setMarkerList(markerData);
          });
        } else {
          getDataRef.current?.(zoomLevelRef.current);
        }
        setIsLastZoomLevel(zoomLevelRef.current === zoomLevelConfig.COUNTY.value);
      }
    });
    scene.map.on('click', () => {
      lxReport('mapClick', {});
    });
    scene.map.on('moveend', () => {
      const bounds = scene.map.getBounds();
      setViewBounds(bounds);
    });
    // 监听 zoomend：动画真正完成时执行挂载的回调（替代魔法 setTimeout）
    scene.map.on('zoomend', () => {
      if (onZoomEndCallbackRef.current) {
        const cb = onZoomEndCallbackRef.current;
        onZoomEndCallbackRef.current = null;
        cb();
      }
    });
  };

  // 点击操场事件
  const onClickPG = async (item: PlayGroundResDataType, options: MarkerProps) => {
    const { lnglat, adCode } = options;
    // 点击其他操场时取消搜索高亮
    if (highlightSerialNo && item.serialNo !== highlightSerialNo) {
      setHighlightSerialNo(undefined);
    }
    if (isLastZoomLevel || item.count === 1) {
      setDetailSerialNumber(item.serialNo);
      setDetailVisible(true);
      lxReport('cardOpen', { item });
    } else {
      const zoomLevel = zoomLevelRef.current;
      if (!mtvSceneRef.current) return;
      const nextMapLevel = getPrevStep(zoomLevel);
      const currentLevelConfig = getCurrentStepInfo(nextMapLevel);
      if (nextMapLevel && nextMapLevel !== zoomLevelRef.current) {
        setLoading(true);
        try {
          let dataList: PlayGroundResDataType[];
          if (selectedProvinceRef.current) {
            dataList = await getMarkerDataList({
              acType: nextMapLevel,
              provinceAdCode: selectedProvinceRef.current.adCode,
              cityAdCode: selectedCityRef.current?.adCode,
              lanType: lanType || '',
            });
          } else {
            dataList = await getMarkerDataList({ acType: nextMapLevel, lanType: lanType || '' });
          }
          const arr = dataList?.filter((_) => _.parentAdCode === adCode);
          if (arr.length === 0) {
            mtvSceneRef.current.setZoomAndCenter(currentLevelConfig.current, [lnglat.lng, lnglat.lat]);
            return;
          }
          const _item = arr[0];
          mtvSceneRef.current.setZoomAndCenter(currentLevelConfig.current, [_item.longitude, _item.latitude]);
        } finally {
          setLoading(false);
        }
      } else {
        mtvSceneRef.current.setZoomAndCenter(currentLevelConfig.current, [lnglat.lng, lnglat.lat]);
      }
    }
    lxReport('mapMarkerClick', { item });
  };

  const onTriggerFullScreen = () => {
    const state = !isFullScreen;
    const center = mtvSceneRef.current.map.getCenter();
    setFullScreen(state);
    if (mtvSceneRef.current) {
      setTimeout(() => {
        mtvSceneRef.current.panBy(0, 0);
        mtvSceneRef.current.setCenter(center);
      }, 30);
    }
    lxReport('fullScreenClick', { state });
    onFullScreenChange?.(state);
  };

  const onAddZoom = () => {
    const currentLevelConfig = getCurrentLevelConfig(zoomMtvLevelRef.current);
    if (currentLevelConfig && mtvSceneRef.current) {
      const { value } = currentLevelConfig;
      const levelStep = getPrevStep(value);
      const center = mtvSceneRef.current.map.getCenter();
      if (levelStep) {
        const { current } = getCurrentStepInfo(levelStep);
        mtvSceneRef.current.setZoomAndCenter(current, center);
      }
    }
    setSelectedProvince(null);
    setSelectedCity(null);
    setHighlightSerialNo(undefined);
  };

  const onReduceZoom = () => {
    const currentLevelConfig = getCurrentLevelConfig(zoomMtvLevelRef.current);
    if (currentLevelConfig && mtvSceneRef.current) {
      const { value } = currentLevelConfig;
      const levelStep = getNextStep(value);
      const center = mtvSceneRef.current.map.getCenter();
      if (levelStep) {
        const { current } = getCurrentStepInfo(levelStep);
        mtvSceneRef.current.setZoomAndCenter(current, center);
      }
    }
    setSelectedProvince(null);
    setSelectedCity(null);
    setHighlightSerialNo(undefined);
  };

  // 初始化
  useEffect(() => {
    setLoading(true);
    registerRef.current?.((state: boolean) => {
      setFullScreen(state);
      lxReport('fullScreenClick', { state: false });
      if (mtvSceneRef.current) {
        const center = mtvSceneRef.current.map.getCenter();
        setTimeout(() => {
          mtvSceneRef.current.panBy(0, 0);
          mtvSceneRef.current.setCenter(center);
        }, 30);
      }
    });
    getDataRef.current?.(initialMapLevel || zoomLevelConfig.COUNTRY.value);
    return () => {
      mtvSceneRef.current?.destroy();
    };
  }, [lxReport, setLoading, initialMapLevel]);

  // 加载省份列表
  useEffect(() => {
    if (!lanType) return;
    listPlaygroundProvinceCityOptions({ lanType })
      .then((res) => {
        setProvinceOptions(res.data || []);
      })
      .catch(() => {
        message.error('加载省份列表失败');
      });
  }, [lanType]);

  // 语言切换时，重新请求地图统计数据（tCharityService_statisticPlaygroundData 需感知语言）
  useEffect(() => {
    if (!lanType || !mtvSceneRef.current) return;
    const currentLevel = zoomLevelRef.current;
    const province = selectedProvinceRef.current;
    const city = selectedCityRef.current;
    if (province) {
      // 有省/市筛选时，按当前筛选条件重新拉数据
      getMarkerDataList({
        acType: currentLevel,
        provinceAdCode: province.adCode,
        cityAdCode: city?.adCode,
        lanType,
      }).then((markerData) => {
        setMarkerList(markerData);
      });
      // 同步刷新城市列表（语言变化后城市名称也需更新）
      if (province) {
        listPlaygroundProvinceCityOptions({ lanType, provinceCode: province.adCode })
          .then((res) => setCityOptions(res.data || []))
          .catch(() => {});
      }
    } else {
      // 无筛选时，按当前地图层级重新拉数据
      getDataRef.current?.(currentLevel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lanType]);

  // 省份选择
  const onProvinceSelect = useCallback(
    async (province: AdminDivisionOptionDto) => {
      // 若此前已选过该省且 count 已经回填，保留旧 count（防止切换端时显示“筛选中”）
      setSelectedProvince((prev) =>
        prev?.adCode === province.adCode && prev.count !== undefined ? { ...province, count: prev.count } : province,
      );
      setSelectedCity(null);
      setCityOptions([]);
      setHighlightSerialNo(undefined);
      // 递增版本号：使正在飞行中的旧请求（如初始化的 COUNTRY 请求）过期失效，防止其响应覆盖本次筛选结果
      markerGenRef.current += 1;
      const gen = markerGenRef.current;
      // 逐级请求城市列表：provinceCode 传省的 adCode（国标编码），不是 id
      listPlaygroundProvinceCityOptions({ lanType: lanType || '', provinceCode: province.adCode })
        .then((res) => setCityOptions(res.data || []))
        .catch(() => {
          message.error('加载城市列表失败');
        });
      // 切换地图到省级视角
      setLoading(true);
      try {
        // 统计接口：provinceAdCode 传省的 adCode（国标编码），不是 id
        const pgStatsLists = await getMarkerDataList({
          acType: MapLevel.PROVINCE,
          provinceAdCode: province.adCode,
          lanType: lanType || '',
        });
        // 若期间触发了新的筛选/清除操作，丢弃过期响应
        if (gen !== markerGenRef.current) return;
        setMarkerList(pgStatsLists);
        if (pgStatsLists.length > 0 && mtvSceneRef.current) {
          // 屏蔽 setZoomAndCenter 动画途中触发的 zoomchange，防止中间层级（如 COUNTRY）再次发出数据请求
          ignoreZoomChangeRef.current = true;
          onZoomEndCallbackRef.current = () => {
            ignoreZoomChangeRef.current = false;
          };
          mtvSceneRef.current.setZoomAndCenter(zoomLevelConfig.PROVINCE.current, [
            pgStatsLists[0].longitude,
            pgStatsLists[0].latitude,
          ]);
        }
        zoomLevelRef.current = MapLevel.PROVINCE;
        setIsLastZoomLevel(false);
        // 直接复用主流程已拿到的 pgStatsLists 计算操场总数并回填，避免额外发起重复请求
        const provinceTotal = pgStatsLists.reduce((sum, m) => sum + (m.count || 0), 0);
        setSelectedProvince((prev) => (prev ? { ...prev, count: provinceTotal } : prev));
      } catch {
        message.error('加载地图数据失败');
      } finally {
        setLoading(false);
      }
    },
    [lanType, setLoading],
  );

  // 城市选择
  const onCitySelect = useCallback(
    async (city: AdminDivisionOptionDto | null) => {
      setSelectedCity(city);
      setHighlightSerialNo(undefined);
      const acType = city ? MapLevel.CITY : MapLevel.PROVINCE;
      const zoomLevel = city ? zoomLevelConfig.CITY.current : zoomLevelConfig.PROVINCE.current;
      // 递增版本号：使正在飞行中的旧请求过期失效，防止其响应覆盖本次筛选结果
      markerGenRef.current += 1;
      const gen = markerGenRef.current;
      setLoading(true);
      try {
        // 统计接口：provinceAdCode/cityAdCode 传 adCode（国标编码），不是 id
        const pgStatsLists = await getMarkerDataList({
          acType,
          provinceAdCode: selectedProvince?.adCode,
          cityAdCode: city?.adCode,
          lanType: lanType || '',
        });
        // 若期间触发了新的筛选/清除操作，丢弃过期响应
        if (gen !== markerGenRef.current) return;
        setMarkerList(pgStatsLists);
        if (pgStatsLists.length > 0 && mtvSceneRef.current) {
          // 屏蔽 setZoomAndCenter 动画途中触发的 zoomchange，防止中间层级再次发出数据请求
          ignoreZoomChangeRef.current = true;
          onZoomEndCallbackRef.current = () => {
            ignoreZoomChangeRef.current = false;
          };
          mtvSceneRef.current.setZoomAndCenter(zoomLevel, [pgStatsLists[0].longitude, pgStatsLists[0].latitude]);
        }
        zoomLevelRef.current = acType;
        setIsLastZoomLevel(false);
        // 直接复用主流程已拿到的 pgStatsLists 计算操场总数并回填，避免额外发起重复请求
        const total = pgStatsLists.reduce((sum, m) => sum + (m.count || 0), 0);
        if (city) {
          setSelectedCity((prev) => (prev ? { ...prev, count: total } : prev));
        } else if (selectedProvince) {
          setSelectedProvince((prev) => (prev ? { ...prev, count: total } : prev));
        }
      } catch {
        message.error('加载地图数据失败');
      } finally {
        setLoading(false);
      }
    },
    [lanType, selectedProvince, setLoading],
  );

  // 搜索结果点击：屏蔽跳转过程中所有中间层级的 zoomchange 请求，zoomend 后直接请求 COUNTY 数据
  const onSearchResultClick = useCallback(
    (item: PlaygroundDetailItem) => {
      setSelectedProvince(null);
      setSelectedCity(null);
      setHighlightSerialNo(item.pgNo);
      const lng = item.longitude ? parseFloat(item.longitude) : null;
      const lat = item.latitude ? parseFloat(item.latitude) : null;
      if (mtvSceneRef.current) {
        // 屏蔽跳转途中的 zoomchange 请求，防止中间层级数据覆盖最终结果
        ignoreZoomChangeRef.current = true;
        // 动画完成（zoomend）后：放开 zoomchange，更新层级，拉取 COUNTY 数据
        onZoomEndCallbackRef.current = () => {
          ignoreZoomChangeRef.current = false;
          zoomLevelRef.current = MapLevel.COUNTY;
          setIsLastZoomLevel(true);
          getDataRef.current?.(MapLevel.COUNTY);
        };
        if (lng && lat) {
          mtvSceneRef.current.setZoomAndCenter(zoomLevelConfig.COUNTY.current, [lng, lat]);
        } else {
          mtvSceneRef.current.setZoom(zoomLevelConfig.COUNTY.current);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lanType],
  );

  // 清除筛选（含重置地图层级到全国视角）
  const onClear = useCallback(() => {
    // 屏蔽回缩途中所有 zoomchange 请求，防止中间层级数据覆盖全国数据
    // 清除上一个待执行的 zoomend 回调（如 onSearchResultClick 设置的），避免竞争
    onZoomEndCallbackRef.current = null;
    ignoreZoomChangeRef.current = true;
    // 递增版本号：使所有正在进行中的过期请求响应失效
    markerGenRef.current += 1;
    setSelectedProvince(null);
    setSelectedCity(null);
    setHighlightSerialNo(undefined);
    getDataRef.current?.(MapLevel.COUNTRY);
    if (mtvSceneRef.current) {
      // 动画完成（zoomend）后放开 zoomchange，后续手动操作可正常触发
      onZoomEndCallbackRef.current = () => {
        ignoreZoomChangeRef.current = false;
      };
      mtvSceneRef.current.setZoomAndCenter(zoomLevelConfig.COUNTRY.current, defaultCenter);
    } else {
      ignoreZoomChangeRef.current = false;
    }
    zoomLevelRef.current = MapLevel.COUNTRY;
    setIsLastZoomLevel(false);
  }, []);

  const showMarkerList: Array<any> = useMemo(() => {
    if (!Array.isArray(markerLists)) return [];
    const mapLevel = zoomLevelRef.current;
    if ([MapLevel.COUNTY, MapLevel.CITY].includes(mapLevel) && viewBounds) {
      return filterPointByBounds({ data: markerLists, bounds: viewBounds });
    }
    return markerLists;
  }, [markerLists, viewBounds]);

  const markerListsNode = showMarkerList.map((markerItemData: PlayGroundResDataType) => (
    <PGMarkerItemNew
      key={`${markerItemData.serialNo}_${markerItemData.adCode}_${markerItemData.parentAdCode}`}
      data={markerItemData}
      showCount={!isLastZoomLevel}
      highlightSerialNo={highlightSerialNo}
      onClick={onClickPG}
    />
  ));

  return (
    <Spin
      wrapperClassName={classNames(styles['pgmap-wrapper'], isFullScreen && styles.fullscreen)}
      spinning={loading}
      delay={500}
    >
      {/* H5：FilterPanel 在地图上方，全屏时隐藏 */}
      {isMobile && !isFullScreen && (
        <FilterPanel
          isMobile
          provinceOptions={provinceOptions}
          cityOptions={cityOptions}
          selectedProvince={selectedProvince}
          selectedCity={selectedCity}
          lanType={lanType || ''}
          mapLoading={loading}
          selectedSearchItem={selectedSearchItem}
          searchKeyword={searchKeyword}
          onSearchKeywordChange={setSearchKeyword}
          onSelectedSearchItemChange={setSelectedSearchItem}
          filterMode={filterMode}
          onFilterModeChange={setFilterMode}
          onProvinceSelect={onProvinceSelect}
          onCitySelect={onCitySelect}
          onSearchResultClick={onSearchResultClick}
          onClear={onClear}
        />
      )}
      {/* 地图主体 */}
      <MtvMap sceneConfig={sceneConfig} mapConfig={mapConfig} onLoaded={onMapLoad}>
        {markerListsNode}
      </MtvMap>
      {/* PC：FilterPanel 常驻在地图左上角 */}
      {!isMobile && (
        <FilterPanel
          isMobile={false}
          provinceOptions={provinceOptions}
          cityOptions={cityOptions}
          selectedProvince={selectedProvince}
          selectedCity={selectedCity}
          lanType={lanType || ''}
          mapLoading={loading}
          selectedSearchItem={selectedSearchItem}
          searchKeyword={searchKeyword}
          onSearchKeywordChange={setSearchKeyword}
          onSelectedSearchItemChange={setSelectedSearchItem}
          filterMode={filterMode}
          onFilterModeChange={setFilterMode}
          onProvinceSelect={onProvinceSelect}
          onCitySelect={onCitySelect}
          onSearchResultClick={onSearchResultClick}
          onClear={onClear}
        />
      )}
      <ZoomControl onAddZoom={onAddZoom} onReduceZoom={onReduceZoom} />
      {/* H5 全屏切换按钮，始终渲染在地图容器内右下角 */}
      <div
        className={classNames(styles['btns-h5'], {
          [`${styles.isOpen}`]: isFullScreen,
        })}
      >
        <div className={styles['fullscreen-btn']} onClick={onTriggerFullScreen}>
          <MTDIcon
            height="24px"
            width="24px"
            icon={isFullScreen ? 'mtdicon-exit-fullscreen-o' : 'mtdicon-fullscreen-o'}
          />
        </div>
      </div>
      <PGDetailModal
        visible={detailVisible}
        lanType={lanType}
        serialNumber={detailSerialNumber}
        onClose={() => {
          setDetailVisible(false);
          lxReport('popupClose', { detailSerialNumber });
        }}
        width={760}
        changeTab={(params: any) => {
          lxReport('popupTabToggle', params);
        }}
        opts={opts}
      />
    </Spin>
  );
};

PGMapNew.displayName = 'PGMapNew';

export default PGMapNew;
